import React, { useState, useEffect, useMemo } from "react";
import { useConfig } from "@peracto/peracto-config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBoxFull } from "@fortawesome/pro-regular-svg-icons/faBoxFull";
import algoliasearch from "algoliasearch/lite";
import { InstantSearch, connectHits, Configure } from "react-instantsearch-dom";
import isEmpty from "lodash/isEmpty";
import { formatPrice } from "../utils";
import { buttonVariants } from "theme/buttonVariants";
import cn from "classnames";

import styled from "@emotion/styled";

const ScrollbarStyles = styled.div`
    ::-webkit-scrollbar {
        height: 20px;
    }

    /* 4px scroll thumb. 10px margin from right margin */
    ::-webkit-scrollbar-track {
        box-shadow: inset 0 0 15px 15px #000;
        border-top: solid 15px transparent;
    }

    ::-webkit-scrollbar-thumb {
        box-shadow: inset 0 0 15px 15px #333;
        border-top: solid 15px transparent;
    }

    ::-webkit-scrollbar-button {
        display: none;
    }
`;

// Attributes that don't start with 'attributes.*'
const UNPREFIXED_ATTRIBUTES = ["sku", "product_type"];

let Icon = () => (
    <div className="flex items-center justify-center w-full py-10">
        <FontAwesomeIcon icon={faBoxFull} size="6x" />
    </div>
);

const ProductCard = ({
    product,
    isEditMode,
    attributes,
    attributeOperator,
    border,
    index,
    ...restProps
}) => {
    const price =
        product?.fromPrices?.price?.inc ||
        product?.prices?.cheapestPrice?.inc ||
        product?.prices?.price?.inc ||
        0;

    const name = product?.attributes?.product_name;
    const availableVariants = product?.variants;

    const image = product?.resources?.find(
        (resource) => resource?.type === "image"
    )?.location;

    return (
        <div
            data-testid="product-card"
            className="product-card flex h-full w-full flex-col justify-between rounded-[5px] border bg-brand-white"
        >
            <div>
                <a
                    data-testid="product-card__link"
                    href={`/products/${product.slug}`}
                    className="relative flex aspect-[307/250] flex-col gap-y-[10px] object-scale-down object-center"
                >
                    <img
                        data-testid="product-card__image"
                        src={image || "/images/placeholder.png"}
                        alt={name}
                        className="h-full w-full"
                    />
                </a>
            </div>

            <div className="flex h-full flex-col p-[15px]">
                <div className="mb-[15px] flex flex-col gap-y-[15px]">
                    <h5
                        data-testid="product-card__name"
                        className={cn(
                            "mb-0 font-heading text-base text-brand-primary md:text-xl md:leading-[26px] flex-grow"
                        )}
                    >
                        {name}
                    </h5>

                    <div>
                        {price && (
                            <div className="flex items-center gap-x-[10px]">
                                <span
                                    data-testid="product-card__now-price"
                                    className="flex flex-row items-center gap-[10px]"
                                >
                                    <span className="text-base leading-[22px]">
                                        {availableVariants ? "From " : "Price "}
                                    </span>
                                    <span
                                        className={cn(
                                            "font-heading text-brand-primary text-base md:text-xl"
                                        )}
                                    >
                                        {formatPrice(price)}
                                    </span>
                                    {/* <TaxMessage incTax={incTax} /> */}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex flex-col gap-y-[15px]">
                    <table>
                        <tbody>
                            <tr>
                                <td className="w-[100px] !py-[3.5px] !pl-[5px] !pr-0 text-xs">
                                    Bed Size:
                                </td>
                                <td className="!py-[3.5px] !pl-0 !pr-[5px] font-heading text-xs">
                                    2.5m x 1.3m (8&apos;2” x 4&apos;1½”)
                                </td>
                            </tr>
                            <tr>
                                <td className="w-[100px] !py-[3.5px] !pl-[5px] !pr-0 text-xs">
                                    Axels:
                                </td>
                                <td className="!py-[3.5px] !pl-0 !pr-[5px] font-heading text-xs">
                                    2
                                </td>
                            </tr>
                            <tr>
                                <td className="w-[100px] !py-[3.5px] !pl-[5px] !pr-0 text-xs">
                                    Gross Weight:
                                </td>
                                <td className="!py-[3.5px] !pl-0 !pr-[5px] font-heading text-xs">
                                    2,700kg
                                </td>
                            </tr>
                            <tr>
                                <td className="w-[100px] !py-[3.5px] !pl-[5px] !pr-0 text-xs">
                                    Payload Weight:
                                </td>
                                <td className="!py-[3.5px] !pl-0 !pr-[5px] font-heading text-xs">
                                    2,240kg
                                </td>
                            </tr>
                            <tr>
                                <td className="w-[100px] !py-[3.5px] !pl-[5px] !pr-0 text-xs">
                                    Wheel Size:
                                </td>
                                <td className="!py-[3.5px] !pl-0 !pr-[5px] font-heading text-xs">
                                    300mm (12”)
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <button
                        data-testid="product-card__add-to-basket"
                        className={cn(
                            "!border-transparent",
                            buttonVariants.primary.solidClasses
                        )}
                    >
                        View product
                    </button>
                </div>
            </div>
        </div>
    );
};

const Hits = (props) => {
    const { hits, isEditMode, fallbackImage, border } = props;

    const inStorefront = !window?.location?.pathname.includes("/content/edit/");

    return hits.length > 0 ? (
        <>
            {hits.map((hit, index) => (
                <ProductCard
                    key={hit?.slug || `hit_${index}`}
                    product={hit}
                    isEditMode={isEditMode}
                    fallbackImage={fallbackImage}
                    border={border}
                    index={index}
                />
            ))}
        </>
    ) : isEditMode || !inStorefront ? (
        <Icon />
    ) : null;
};
const CustomHits = connectHits(Hits);

const ProductsContent = ({
    numberOfProducts = 4,
    attributes = [],
    attributeOperator = "and",
    border,
    isEditMode,
}) => {
    const config = useConfig();
    const algolia = config.get("algolia") || {};

    const { applicationId, searchApiKey, indexName } = algolia;

    const searchClient = algoliasearch(applicationId, searchApiKey);

    const [facetFilters, setFacetFilters] = useState();

    useEffect(() => {
        if (attributes?.length > 0) {
            const categories = attributes?.reduce(
                (acc, { attribute, value }) => {
                    const attributeKey = UNPREFIXED_ATTRIBUTES.includes(
                        attribute
                    )
                        ? attribute
                        : `attributes.${attribute}`;
                    if (value.length > 0) acc.push(`${attributeKey}:${value}`);

                    return acc;
                },
                []
            );

            setFacetFilters(categories);
        }
    }, [attributes]);

    if (isEmpty(algolia)) {
        console.error(
            "No Algolia configuration defined. Please refer to the Peracto documentation."
        );
    }

    return useMemo(
        () =>
            isEmpty(algolia) ? null : (
                <InstantSearch
                    searchClient={searchClient}
                    indexName={indexName}
                >
                    <Configure
                        hitsPerPage={numberOfProducts}
                        facetFilters={
                            facetFilters
                                ? attributeOperator === "and"
                                    ? facetFilters
                                    : [facetFilters]
                                : null
                        }
                    />

                    <CustomHits isEditMode={isEditMode} border={border} />
                </InstantSearch>
            ),
        [
            algolia,
            searchClient,
            indexName,
            numberOfProducts,
            border,
            facetFilters,
            attributeOperator,
            isEditMode,
        ]
    );
};

const ProductsRenderer = ({ state, ...props }) => {
    return (
        <ScrollbarStyles className="flex flex-row gap-[30px] flex-nowrap overflow-auto product-card-container content__products">
            <ProductsContent {...state} {...props} />
        </ScrollbarStyles>
    );
};

export default ProductsRenderer;
